<template>
  <div id="dropdown" class="custom-dropdown-container">
    <div class="custom-select pointer" :class="{ compact , disabled}">
      <div class="selector" :data-validation="dataValidation" ref="dropdownParent" @mouseleave="!enableSearch && (visible = false)">
        <div class="select-label show-error-bdr"   @click="toggle">
          <slot name="label" :item="currentSelected" :default-label="defaultLabel">
            <div class="elipsis-text mr-4" style="width: calc(100% - 30px); white-space: nowrap; overflow: hidden; text-overflow: ellipsis;" :title="currentSelected?.name ?? defaultLabel">
              {{ currentSelected?.name ?? defaultLabel  }}
            </div>
          </slot>
        </div>
            <svg class="dropdown-icon" @click.stop="toggle" :class="{ 'rotated': visible }" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
        <div
          class="dropdown"
          :class="[
            { hidden: !visible, visible },
            openUp ? 'dropdown-up' : 'dropdown-down'
          ]"
          :style="dropdownStyle"
        >
          <div class="dropdown-content" :class="{ 'upward': openUp }" @mouseleave="(e)=>e.stopPropagation()" @mousemove="disableMouseOver=false">
            <div v-if="!openUp" class="slot-container">
              <slot name="before-items"></slot>
            </div>

            <div v-if="enableSearch" class="dropdown-search">
              <input
                ref="searchInput"
                v-model="internalSearchText"
                class="dropdown-search__input"
                placeholder="Search..."
                @click.stop
                @keydown.stop
              />
            </div>

            <ul ref="dropdownList">
              <div class="notMatching" v-if="filteredList.length === 0 && !isLoading">No items found</div>
              <li
                v-else
                :class="{
                  current: item.id === currentSelected?.id,
                  'v-center': true,
                  'highlight-item': index === highlightedIndex,
                  disabled: item.disabled
                }"
                v-for="(item, index) in filteredList"
                :key="item?.id"
                :ref="item.id"
                @click.stop="!item.disabled && select(item)"
                @mouseover="!item.disabled && (highlightedIndex = disableMouseOver ? highlightedIndex : index)"
                >
                <div class="w-100">
                 <slot name="item" :item="item">
                   <div class="elipsis-text list-name" v-overflow-tooltip> {{ item?.name }}</div>
                   <div class="xs elipsis-text list-name" v-if="showDescription" v-overflow-tooltip>({{ item?.description }})</div>
                  </slot>
                </div>
              </li>
              <li
                v-if="infiniteScroll && hasMore"
                ref="loadMoreTrigger"
                class="load-more-trigger"
              >
                <span v-if="isLoading">Loading...</span>
              </li>
            </ul>

            <div v-if="openUp" class="slot-container">
              <slot name="before-items"></slot>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { removeErrorBorder } from '@/helper/formValidation/errorBorder'

export default {
  name: 'CustomDropdown',
  props: {
    compact: {
      type: Boolean,
      default: false
    },
    list: {
      default: () => [],
      type: Array
    },
    selected: {
      default: () => ({ id: -1, name: 'Select' }),
      type: Object
    },
    searchText: {
      default: '',
      type: String
    },
    showDescription: {
      default: false,
      type: Boolean
    },
    index: {
      type: Number,
      default: 0
    },
    styleParameters: {
      type: Object,
      default: () => ({})
    },
    dataValidation: {
      type: String,
      default: ''
    },
    defaultStyle: {
      type: Object,
      default: () => ({})
    },
    defaultLabel: {
      type: String,
      default: 'Select'
    },
    toggleDropDown: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    preventDeselection: {
      type: Boolean,
      default: false
    },
    enableSearch: {
      type: Boolean,
      default: false
    },
    infiniteScroll: {
      type: Boolean,
      default: false
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    hasMore: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      visible: false,
      openUp: false,
      dropdownStyle: {},
      highlightedIndex: -1, // New data property for keyboard navigation
      disableMouseOver: false,
      currentSelected: null,
      internalSearchText: '',
      observer: null
    }
  },
  computed: {
    filteredList () {
      // When enableSearch is on, use the internal text; otherwise fall back to the external searchText prop
      const query = this.enableSearch ? this.internalSearchText : this.searchText
      if (this.infiniteScroll) return this.list
      if (!query.length) return this.list
      if (this.showDescription) {
        return this.list.filter(item => {
          return (
            item?.name.toLowerCase().includes(query.toLowerCase()) ||
            item.description.toLowerCase().includes(query.toLowerCase())
          )
        })
      } else {
        return this.list.filter(item => {
          return item?.name.toLowerCase().includes(query.toLowerCase())
        })
      }
    },
    highlightedItem () {
      return this.filteredList[this.highlightedIndex]
    }
  },
  methods: {
    syncSelected (value = this.selected) {
      const next = value && Object.keys(value).length ? { ...value } : null
      if (!next) {
        this.currentSelected = null
        return
      }
      if (!next?.name && next?.id !== undefined && next?.id !== null) {
        const item = this.list.find(item => item.id === next.id)
        if (item) {
          this.currentSelected = { ...item }
          return
        }
      } else if ((next?.id === undefined || next?.id === null) && next?.name) {
        this.currentSelected = null
        return
      }
      this.currentSelected = next
    },
    toggle (el) {
      if (this.disabled) return
      this.visible = !this.visible
      if (this.visible) {
        // Reset highlighted index when opening, or set to current selection
        const selectedIndex = this.filteredList.findIndex(item => item.id === this.currentSelected?.id)
        this.highlightedIndex = selectedIndex !== -1 ? selectedIndex : 0
        this.addKeyListener()

        const triggerElement = el.target
        const parentElement = triggerElement.parentElement
        const parentRect = parentElement.getBoundingClientRect()
        let found = false
        let parent = parentElement
        if (this.$el.closest('.project-form-slider')) {
          while (parent) {
            if (window.getComputedStyle(parent).position === 'fixed') {
              found = true
              break
            }
            parent = parent.parentElement
          }
        }
        let parentBottom = parentRect.bottom
        let parentLeft = parentRect.left
        let modalBottom = null
        if (found) {
          const parentRect = this.$refs.dropdownParent.getBoundingClientRect()
          const modalEl = this.$el.closest('.project-form-slider')
          // const modalScrollEl = this.$el.closest('.modalScroll')
          const modalRect = modalEl.getBoundingClientRect()
          // const top = parentRect.top - modalRect.top
          // const left = parentRect.left - modalRect.left
          // const scrollTop = modalScrollEl.scrollTop
          // const scrollLeft = this.$refs.scrollContainer.scrollLeft
          parentBottom = parentRect.bottom - modalRect.top
          parentLeft = parentRect.left - modalRect.left
          modalBottom = modalRect.bottom
        }
        // Usage)
        this.checkDropdownDirection(parentElement?.offsetWidth, parentBottom, parentLeft, modalBottom)
        this.setupIntersectionObserver()
      } else {
        this.removeKeyListener()
        this.dropdownStyle = {} // Clean up
        this.internalSearchText = ''
        if (this.observer) this.observer.disconnect()
      }
      this.$emit('toggle')
    },
    select (item) {
      if (item.id === this.currentSelected?.id) {
        if (!this.preventDeselection) this.deselect()
        else this.toggle()
        return
      }
      if (this.dataValidation) {
        removeErrorBorder([this.dataValidation])
      }
      this.$emit('select', item)
      this.toggle()
    },
    deselect () {
      this.$emit('deselect')
      this.toggle()
    },
    checkDropdownDirection (widthPassed = undefined, parentBottom = undefined, parentLeft = undefined, modalBottom = null) {
      this.$nextTick(() => {
        const dropdown = this.$el.querySelector('.dropdown')
        const trigger = this.$el.querySelector('.select-label')
        if (!dropdown || !trigger) return

        const triggerRect = trigger.getBoundingClientRect()
        const dropdownHeight = dropdown.offsetHeight
        const spaceBelow = window.innerHeight - triggerRect.bottom
        const spaceAbove = triggerRect.top
        const spaceLeft = parentLeft ?? triggerRect.left
        if (dropdownHeight > spaceBelow && spaceAbove > dropdownHeight) {
          // open upward using fixed position
          this.openUp = true
          let bottomSpace = window.innerHeight - triggerRect.top
          if (modalBottom) {
            bottomSpace = modalBottom - triggerRect.top
          }
          this.dropdownStyle = {
            position: 'fixed',
            bottom: `${ bottomSpace}px`,
            width: `${triggerRect.width}px`,
            zIndex: 9999,
            left: `${spaceLeft}px`,
            overflow: 'visible',
            border: '1px solid gainsboro'
          }
        } else {
          // open downward with default style
          this.openUp = false
          this.dropdownStyle = {
            position: 'fixed',
            top: `${parentBottom}px`,
            left: `${spaceLeft}px`,
            width: `${widthPassed}px`,
            zIndex: 999
          }
        }
        // Scroll to selected/highlighted item on open
        const itemToScroll = this.highlightedItem
        if (itemToScroll) {
          this.scrollToItem(itemToScroll.id)
        }
        // Auto-focus search input when search is enabled
        if (this.enableSearch) {
          this.$nextTick(() => {
            if (this.$refs.searchInput) {
              this.$refs.searchInput.focus()
            }
          })
        }
      })
    },
    // New methods for keyboard navigation
    addKeyListener () {
      window.addEventListener('keydown', this.handleKeyDown)
    },
    removeKeyListener () {
      window.removeEventListener('keydown', this.handleKeyDown)
    },
    handleKeyDown (e) {
      if (!this.visible || this.filteredList.length === 0) return

      switch (e.key) {
      case 'ArrowUp':
        e.preventDefault()
        this.disableMouseOver = true
        this.highlightedIndex = Math.max(0, this.highlightedIndex - 1)
        this.scrollToItem(this.highlightedItem.id)
        break
      case 'ArrowDown':
        e.preventDefault()
        this.disableMouseOver = true
        this.highlightedIndex = Math.min(this.filteredList.length - 1, this.highlightedIndex + 1)
        this.scrollToItem(this.highlightedItem.id)
        break
      case 'Enter':
        e.preventDefault()
        if (this.highlightedItem) {
          this.select(this.highlightedItem)
        }
        break
      case 'Escape':
        e.preventDefault()
        this.visible = false
        this.removeKeyListener()
        break
      }
    },
    scrollToItem (itemId) {
      this.$nextTick(() => {
        const itemEl = this.$refs[itemId]?.[0]
        if (itemEl && itemEl.scrollIntoView) {
          itemEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
        }
      })
    },
    closeOnOutsideClick (e) {
      // console.log(e)
      // if (this.$el.contains(e.target)) {
      // }
      // // Check if the click occurred outside the main dropdown element
      if (this.visible && !this.$el.contains(e.target)) {
        this.visible = false
      }
    },
    defaultStyleUpdate () {
      if (this.defaultStyle.dropDownWidth) {
        this.$el.style.setProperty('--dropDownWidth', this.defaultStyle.dropDownWidth)
      } if (this.defaultStyle.borderColor) {
        this.$el.style.setProperty('--borderColor', this.defaultStyle.borderColor)
      } if (this.defaultStyle.backGroundColor) {
        this.$el.style.setProperty('--backGroundColor', this.defaultStyle.backGroundColor)
      } if (this.defaultStyle.labelHeight) {
        this.$el.style.setProperty('--labelHeight', this.defaultStyle.labelHeight)
      } if (this.defaultStyle.ddLabelFontSize) {
        this.$el.style.setProperty('--ddLabelFontSize', this.defaultStyle.ddLabelFontSize)
      } if (this.defaultStyle.ddMaxWidth) {
        this.$el.style.setProperty('--ddMaxWidth', this.defaultStyle.ddMaxWidth)
      }
    },
    setupIntersectionObserver () {
      if (!this.infiniteScroll) return

      this.$nextTick(() => {
        const trigger = this.$refs.loadMoreTrigger
        const scrollContainer = this.$refs.dropdownList

        if (!trigger || !scrollContainer) return

        // Remove any existing observer
        if (this.observer) {
          this.observer.disconnect()
        }

        this.observer = new IntersectionObserver(
          ([entry]) => {
            if (
              entry.isIntersecting &&
              !this.isLoading &&
              this.hasMore
            ) {
              this.observer.disconnect()
              this.$emit('load-more')
            }
          },
          {
            root: scrollContainer,
            threshold: 0.1
          }
        )

        this.observer.observe(trigger)
      })
    }
  },
  watch: {
    visible () {
      this.$emit('toggle', this.visible)
    },
    // Reset highlightedIndex when search text changes (i.e., filteredList changes)
    searchText () {
      this.highlightedIndex = 0
    },
    internalSearchText (value) {
      this.highlightedIndex = 0
      if (this.infiniteScroll) {
        this.$emit('search-change', value)
      }
    },
    selected: {
      handler (newVal) {
        this.syncSelected(newVal)
      },
      deep: true,
      immediate: true
    },
    list () {
      this.syncSelected()

      if (this.visible) {
        this.setupIntersectionObserver()
      }
    },
    defaultStyle () {
      this.defaultStyleUpdate()
    },
    toggleDropDown () {
      if (this.toggleDropDown) {
        this.toggle(this.$refs.dropdownParent)
      }
    }
  },
  mounted () {
    window.document.addEventListener('click', this.closeOnOutsideClick)
    if (this.styleParameters?.width) {
    }
    this.currentSelected = this.selected
    if (!this.currentSelected?.name && this.currentSelected?.id) {
      this.currentSelected.name = this.list.find(item => item.id === this.currentSelected.id)?.name
    } else if (!this.currentSelected?.id && this.currentSelected?.name) {
      this.currentSelected = {}
    }
    if (this.defaultStyle) {
      this.defaultStyleUpdate()
    }
  },
  beforeDestroy () {
    window.document.removeEventListener('click', this.closeOnOutsideClick)
    this.removeKeyListener()
    if (this.observer) this.observer.disconnect()
  }
}
</script>

<style lang="scss">
:root{
  --borderColor: #d1d5db;
  --dropDownWidth:100%;
  --backGroundColor: transparent;
  --labelHeight: 34.2px;
  --ddLabelFontSize:1em;
  // --ddMaxWidth: 400px;
}
.custom-select {
  .disabled{
  pointer-events: none;
  opacity: 0.6;
  }
  width:var(--dropDownWidth);
  // min-height:  var(--labelHeight);
  max-width: var(--ddMaxWidth);
  .selector {
    position: relative;
    // background-color: white;

    .select-label {
      display: block;
      border: 1px solid var(--borderColor);
      border-radius: 0.285em;
      font-size: var(--ddLabelFontSize);
      // padding: 6.5px 0.68em;
      background-color: var(--backGroundColor);
      height: var(--labelHeight);
    display: flex;
    align-items: center;
    padding: 0 0 0 8px;
    }

    .dropdown {
      position: fixed;
      z-index: 999;
      // width: 100%;
      background: white;
      border: 1px solid rgb(243, 241, 241);
      overflow: hidden;
      max-height: 16rem;
      border-radius: 8px;
      box-shadow: rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.06) 0px 2px 4px -1px;

      &.dropdown-down {
        // top: 100%;
      }

      &.dropdown-up {
        bottom: auto;
        top: auto;
      }

      .dropdown-content {
        display: flex;
        flex-direction: column;
        max-height: 16rem;
        overflow: hidden;

        ul {
          overflow: auto;
          flex: 1;
          margin: 0;
          padding: 8px 8px 8px 8px;
          list-style: none;
          font-size: var(--text-xs2);
          font-weight: 400;

          & .list-name{
            display: flex;
            align-items: center;
            gap: 5px;
          }

          li {
            text-align: left;
            line-height: normal;
            padding: 8px;
            color: #000000;
            padding-left: 20px;
            border-radius: 3px;

            &:hover {
              // cursor: pointer;
              // color: white;
              // background: rgb(42, 108, 250, .6);
            }

            li.disabled {
              opacity: 0.5;
              cursor: not-allowed;
              pointer-events: none;
            }

            &.current {
            background-color: #ebebeb;
            transition: background-color 0.3s ease;
              font-weight: 600;
              border: 1px solid rgb(233, 233, 233);
              border-radius: 3px;
              // border-radius: 6px;
            }

            // New style for keyboard highlighted item
            &.highlight-item {
            background-color: #f0f0f0d4;
            transition: background-color 0.3s ease;

            }
          }
        }

        .slot-container {
          border-top: 1px solid #e0e0e0;
          background: white;
        }
      }
    }

    .dropdown-search {
      padding: 6px 8px;
      border-bottom: 1px solid #e8e8e8;
      background: white;
      position: sticky;
      top: 0;
      z-index: 1;

      &__input {
        width: 100%;
        border: 1px solid #d1d5db;
        border-radius: 4px;
        padding: 5px 8px;
        font-size: var(--text-xs2);
        outline: none;
        background: #f9fafb;
        color: #111;
        box-sizing: border-box;
        transition: border-color 0.15s ease;

        &:focus {
          border-color: #6366f1;
          background: #fff;
        }

        &::placeholder {
          color: #9ca3af;
        }
      }
    }

    .hidden {
      visibility: hidden;
    }

    .visible {
      visibility: visible;
    }
  }
}
.custom-select.compact {
  width: 10rem;
  height: fit-content;
  .selector {
    .select-label {
      font-size: 0.75em; // Smaller text
      padding: 0.25em 0.4em; // Less space inside the box
      line-height: 1.2;  // Tighter vertical height
    }
  }
  .dropdown {
    max-height: 10rem;
    .dropdown-content {
      max-height: 10rem;
      ul li {
        padding: 4px 6px;
        font-size: 0.75em; // Smaller option text
        line-height: 1.2;
      }
    }
  }
}
.notMatching{
  color: #888;
  font-size: 0.85em;
  padding: 8px;
  text-align: center;
}
.dropdown-icon {
  width: 15px;
  height: 15px;
  color: #6b7280;
  transition: transform 0.3s ease;
       position: absolute;
      right: 10px;
      top: 50%;
      transform: translateY(-50%);
}

.dropdown-icon.rotated {
  transform: rotate(180deg) translateY(50%);
}
.custom-dropdown-container{
  width:var(--dropDownWidth);
}

</style>
